import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Lock as LockIcon, SmartToy as AIIcon } from "@mui/icons-material";
import { useAiConsent, AI_CONSENT_TEXT } from "../../hooks/useAiConsent";

interface AiConsentGateProps {
  children: React.ReactNode;
  /**
   * Что показать, пока согласие не загружено. По умолчанию — спиннер.
   * fail-closed: до подтверждения согласия дочерние AI-фичи не рендерятся.
   */
  loadingFallback?: React.ReactNode;
}

/**
 * Enforcement-guard для AI-фич (ADR-008 §5 / W5-S2).
 *
 * Default-deny: рендерит детей ТОЛЬКО при наличии согласия `aiProcessing`.
 * Пока статус грузится — fallback; при отсутствии/ошибке чтения — экран
 * запроса согласия (никаких обращений к AI до явного «Разрешить»).
 */
export const AiConsentGate: React.FC<AiConsentGateProps> = ({
  children,
  loadingFallback,
}) => {
  const { hasAiConsent, isLoading, grantAiConsent, isGranting, grantError } =
    useAiConsent();

  if (isLoading) {
    return (
      <>
        {loadingFallback ?? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={28} />
          </Box>
        )}
      </>
    );
  }

  if (hasAiConsent) {
    return <>{children}</>;
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 560, mx: "auto", my: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <LockIcon color="action" />
          <Typography variant="h6">Требуется согласие на AI</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {AI_CONSENT_TEXT}
        </Typography>

        {grantError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Не удалось сохранить согласие. Попробуйте ещё раз.
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={<AIIcon />}
          disabled={isGranting}
          onClick={() => {
            void grantAiConsent().catch(() => {
              /* ошибка отражена через grantError */
            });
          }}
        >
          {isGranting ? "Сохранение…" : "Разрешить AI-обработку"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AiConsentGate;
